import { useMemo } from 'react';
import { StanDropdown } from '@/components';
import type { DropdownMenuOption, DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { formatCurrency } from '@/lib/currency';
import type { FunnelPageItem, FunnelPageProductInfo, FunnelRouteTarget, FunnelRoutingEvent } from '../../../types';

export const LINEAR_VALUE = '__linear__';
export const DELETED_VALUE = '__deleted__';

interface RoutingRowProps {
    event: FunnelRoutingEvent;
    label: string;
    helper: string;
    route: FunnelRouteTarget | null;
    eligibleTargets: FunnelPageItem[];
    claimedByOthers: Map<number, number>;
    claimedByOtherEvent: number | null;
    productInfoByPageId: Record<number, FunnelPageProductInfo | undefined>;
    onChange: (target: FunnelRouteTarget | null) => void;
    disabled?: boolean;
    disabledReason?: string;
}

export const RoutingRow = ({
    event,
    label,
    helper,
    route,
    eligibleTargets,
    claimedByOthers,
    claimedByOtherEvent,
    productInfoByPageId,
    onChange,
    disabled,
    disabledReason,
}: RoutingRowProps) => {
    const targetExists = route ? eligibleTargets.some(p => p.page_id === route.page_id) : true;

    const options: DropdownMenuOption[] = useMemo(() => {
        const base: DropdownMenuOption[] = [{ label: 'Continue to the next step', value: LINEAR_VALUE }];
        eligibleTargets.forEach(target => {
            const info = productInfoByPageId[target.page_id];
            const title = info?.title ?? `Step ${target.order}`;
            const priceLabel =
                typeof info?.price === 'number' ? ` · ${formatCurrency(info.price, info.currency ?? 'USD')}` : '';
            const claimedByOther = claimedByOthers.has(target.page_id);
            const claimedByThisOtherEvent = claimedByOtherEvent === target.page_id;
            const isCurrent = route?.page_id === target.page_id;
            const suffix = (() => {
                if (isCurrent) return '';
                if (claimedByOther) return ' · Already routed from another page';
                if (claimedByThisOtherEvent) return ' · Already used by the other branch';
                return '';
            })();
            base.push({
                label: `Step ${target.order} · ${title}${priceLabel}${suffix}`,
                value: String(target.page_id),
                disabled: !isCurrent && (claimedByOther || claimedByThisOtherEvent),
            });
        });
        if (route && !targetExists) {
            base.push({ label: 'Deleted page — clear route', value: DELETED_VALUE });
        }
        return base;
    }, [eligibleTargets, productInfoByPageId, route, targetExists, claimedByOthers, claimedByOtherEvent]);

    const currentValue: DropdownMenuOption | undefined = useMemo(() => {
        if (!route) return options.find(o => o.value === LINEAR_VALUE);
        if (!targetExists) return { label: 'Deleted page', value: DELETED_VALUE };
        return options.find(o => o.value === String(route.page_id));
    }, [options, route, targetExists]);

    const handleSelect = (_: DropdownValue, option: DropdownMenuOption) => {
        if (option.value === LINEAR_VALUE || option.value === DELETED_VALUE) {
            onChange(null);
            return;
        }
        const pageId = Number(option.value);
        if (!Number.isFinite(pageId)) return;
        const target = eligibleTargets.find(p => p.page_id === pageId);
        const label = target ? productInfoByPageId[target.page_id]?.title : undefined;
        onChange(label ? { page_id: pageId, label } : { page_id: pageId });
    };

    return (
        <div className='flex flex-col gap-2'>
            <div className='flex flex-col gap-0.5'>
                <p className='text-body-sm text-dark font-medium'>{label}</p>
                <p className='text-caption-1-regular text-gray'>{helper}</p>
            </div>
            <StanDropdown
                id={`routing-${event}`}
                name={`routing-${event}`}
                options={options}
                onSelect={handleSelect}
                currentValue={currentValue}
                preventDeselect
                disabled={disabled}
                placeholder='Continue to the next step'
            />
            {disabled && disabledReason && <p className='text-body-sm text-light'>{disabledReason}</p>}
        </div>
    );
};
